import { getRuntimeConfig } from '../config/runtime-config.js';
import { createLogger } from '../utils/logger.js';

/**
* Responsibility:
* - Build the live configurator app state from DOM-embedded runtime config.
*
* Touches:
* - Reads runtime JSON from the supplied configurator root.
* - Creates the logger used by all frontend configurator modules.
*
* Guarantees:
* - Returns `null` when the rendered configurator root does not expose the required runtime payload.
*/
export function bootstrap(configRoot) {
	const config = getRuntimeConfig(configRoot);
	const logger = createLogger(config.debug);

	if (!config.formId) {
		logger.error('Configurator failed to initialize: required runtime form id is missing from the rendered configurator root.', {
			formId: config.formId,
			whatWentWrong: 'Required runtime value `formId` is empty.',
			likelyCause: 'Configurator runtime JSON was not rendered inside the native Gravity Forms form root.',
			howToFix: 'Render the native Gravity Forms form for this page and verify the local `.sd-config-runtime` script node contains a valid JSON payload.',
		});
		return null;
	}

	if (!config.adviceResultEndpoint) {
		logger.error('Configurator failed to initialize: machine advice result endpoint is missing in runtime config.', {
			formId: config.formId,
			whatWentWrong: 'Runtime key `adviceResultEndpoint` is empty.',
			likelyCause: 'Configurator runtime JSON was not rendered correctly for this page.',
			howToFix: 'Verify the configurator root prints the runtime JSON payload and that REST route `/sd-witechs/v1/advice/result` is available.',
		});
		return null;
	}

	if (!config.boundaryClass || !config.adviceContainerClass) {
		logger.error('Configurator failed to initialize: boundary or output container class is missing.', {
			formId: config.formId,
			boundaryClass: config.boundaryClass,
			adviceContainerClass: config.adviceContainerClass,
			whatWentWrong: 'Boundary and/or advice output class was empty in runtime config.',
			likelyCause: 'Form settings for machine advice boundary/output class are incomplete.',
			howToFix: 'Set both settings in form configuration and ensure matching classes exist in the form markup.',
		});
		return null;
	}

	logger.debug('Bootstrap complete.', {
		formId: config.formId,
		boundaryClass: config.boundaryClass,
	});

	return {
		config,
		logger,
	};
}
